import { useMemo } from "react";

import { useReorderAnimation } from "../hooks/useReorderAnimation.js";
import type { AgentConversationDto } from "../api/types.js";

export type QueueFilter = "waiting" | "mine" | "unassigned" | "live" | "all";

const FILTERS: readonly { readonly value: QueueFilter; readonly label: string }[] = [
  // "Waiting" first and selected by default: a customer who has asked for a human
  // and not yet got one is the only genuinely time-critical item in the tool.
  { value: "waiting", label: "Waiting" },
  { value: "mine", label: "Mine" },
  { value: "unassigned", label: "Unclaimed" },
  { value: "live", label: "Live" },
  { value: "all", label: "All" },
];

/** How long ago, in words. Exact timestamps are noise in a queue. */
function ago(iso: string): string {
  const seconds = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 1000));

  if (seconds < 60) return `${String(seconds)}s`;
  if (seconds < 3600) return `${String(Math.floor(seconds / 60))}m`;
  if (seconds < 86_400) return `${String(Math.floor(seconds / 3600))}h`;

  return `${String(Math.floor(seconds / 86_400))}d`;
}

function statusLabel(conversation: AgentConversationDto): { text: string; tone: string } {
  switch (conversation.status) {
    case "awaiting_agent":
      return { text: "Waiting for a human", tone: "awaiting" };
    case "agent_handling":
      return { text: "With an agent", tone: "live" };
    case "closed":
      return { text: "Closed", tone: "closed" };
    case "ai_handling":
      return { text: "Assistant", tone: "" };
  }
}

/** Best available name for the person on the other side. */
/**
 * Who the agent is talking to.
 *
 * The stored name first: a signed-in shopper has one on their account, and until it
 * was captured every row for a real customer said the literal word "Customer" — the
 * person the platform knew most about looked like the one it recognised least.
 */
function requesterName(conversation: AgentConversationDto): string {
  const stored = conversation.requesterDisplayName?.trim();

  if (stored !== undefined && stored !== "") return stored;

  if (conversation.preChat !== undefined) {
    const typed = `${conversation.preChat.firstName} ${conversation.preChat.lastName}`.trim();
    if (typed !== "") return typed;
  }

  return conversation.requester.type === "guest" ? "Guest" : "Customer";
}

/**
 * Initials for the avatar beside a row.
 *
 * A queue is scanned rather than read, and a column of names in one weight has
 * to be read. Two letters give each row a shape an agent recognises before they
 * have finished looking at it.
 *
 * Falls back to a person mark rather than to a letter of a placeholder word:
 * "GU" for every guest would be a column of identical initials, which is worse
 * than no initials at all.
 */
function initials(name: string): string {
  const words = name
    .split(/\s+/)
    .filter((word) => word !== "" && /\p{L}/u.test(word.charAt(0)))
    .slice(0, 2);

  if (words.length === 0) return "·";

  return words.map((word) => word.charAt(0).toUpperCase()).join("");
}

export function QueueList({
  conversations,
  loading,
  error,
  filter,
  onFilterChange,
  selectedId,
  onSelect,
  onRefresh,
  unread,
  selectable = false,
  checked,
  onCheck,
  onCheckAll,
  onDeleteChecked,
  deleting = false,
}: {
  readonly conversations: readonly AgentConversationDto[];
  readonly loading: boolean;
  readonly error: string | null;
  readonly filter: QueueFilter;
  readonly onFilterChange: (filter: QueueFilter) => void;
  /** Messages arrived per conversation since this agent last had it open. */
  readonly unread?: ReadonlyMap<string, number>;
  readonly selectedId: string | null;
  readonly onSelect: (conversationId: string) => void;
  readonly onRefresh: () => void;
  /**
   * Bulk selection, offered only to whoever may act on it.
   *
   * Absent for an agent who cannot delete: a row of checkboxes that leads
   * nowhere is worse than no checkboxes, and this is the one screen where an
   * accidental tick has consequences.
   */
  readonly selectable?: boolean;
  readonly checked?: ReadonlySet<string>;
  readonly onCheck?: (conversationId: string, checked: boolean) => void;
  readonly onCheckAll?: (checked: boolean) => void;
  readonly onDeleteChecked?: () => void;
  readonly deleting?: boolean;
}) {
  const checkedCount = checked?.size ?? 0;
  const allChecked = conversations.length > 0 && checkedCount === conversations.length;

  /**
   * The queue is ordered by most recent activity, so every customer message moves
   * a row. Without this the row an agent is reading jumps somewhere else and they
   * have to find it again — see `useReorderAnimation`.
   */
  const order = useMemo(
    () => conversations.map((conversation) => conversation.conversationId),
    [conversations],
  );
  const { rowRef } = useReorderAnimation(order);

  return (
    <nav className="panel" aria-label="Conversation queue">
      <div className="panel-header">
        {/*
          One control rather than five buttons. Five wrapped onto two rows in a
          narrow panel, and a wrapped row of controls reads as two groups that
          mean different things — this says plainly that one of them is chosen.
        */}
        <div className="segmented" role="group" aria-label="Filter the queue">
          {FILTERS.map((entry) => (
            <button
              key={entry.value}
              aria-pressed={filter === entry.value}
              onClick={() => {
                onFilterChange(entry.value);
              }}
            >
              {entry.label}
            </button>
          ))}
        </div>
        <button className="icon" onClick={onRefresh} title="Refresh" aria-label="Refresh the queue">
          ↻
        </button>
      </div>

      {/*
        Appears only once something is ticked.
        A permanently visible bar would put a delete control on screen at all
        times for a queue nobody has selected anything in.
      */}
      {selectable && checkedCount > 0 && (
        <div className="panel-header bulk-bar">
          <label className="small">
            <input
              type="checkbox"
              checked={allChecked}
              onChange={(event) => onCheckAll?.(event.target.checked)}
            />{" "}
            {allChecked ? "All" : checkedCount} selected
          </label>
          <span className="spacer" />
          <button
            className="danger"
            disabled={deleting}
            onClick={onDeleteChecked}
            title="Permanently delete the selected conversations"
          >
            {deleting ? "Deleting…" : `Delete ${String(checkedCount)}`}
          </button>
          <button disabled={deleting} onClick={() => onCheckAll?.(false)}>
            Clear
          </button>
        </div>
      )}

      <div className="scroll">
        {loading && (
          <p className="muted small" style={{ padding: 14 }}>
            Loading…
          </p>
        )}
        {error !== null && (
          <p className="error" style={{ padding: 14 }}>
            {error}
          </p>
        )}

        {!loading && error === null && conversations.length === 0 && (
          <p className="muted small" style={{ padding: 14 }}>
            Nothing here.
          </p>
        )}

        {conversations.map((conversation) => {
          const status = statusLabel(conversation);
          const name = requesterName(conversation);
          const newMessages = unread?.get(conversation.conversationId) ?? 0;

          return (
            <div
              className="queue-row"
              key={conversation.conversationId}
              ref={rowRef(conversation.conversationId)}
            >
              {/*
                A sibling of the row, not a child of it. The row is a <button>,
                and a checkbox nested inside one is both invalid and unreachable
                by keyboard — every tick would open the conversation instead.
              */}
              {selectable && (
                <input
                  type="checkbox"
                  className="queue-check"
                  checked={checked?.has(conversation.conversationId) ?? false}
                  disabled={deleting}
                  aria-label={`Select the conversation with ${name}`}
                  onChange={(event) => onCheck?.(conversation.conversationId, event.target.checked)}
                />
              )}

              {/*
                Spans rather than divs throughout: this is a <button>, whose
                content model is phrasing, and the layout comes from CSS either
                way. Nesting flow content in a control is the kind of markup
                that works until an assistive technology reads it.
              */}
              <button
                className={newMessages > 0 ? "queue-item is-unread" : "queue-item"}
                aria-current={conversation.conversationId === selectedId}
                onClick={() => {
                  onSelect(conversation.conversationId);
                }}
              >
                <span className="queue-avatar" aria-hidden="true">
                  {initials(name)}
                </span>

                <span className="queue-body">
                  <span className="queue-top">
                    <span className="queue-name">{name}</span>
                    <span className="queue-time">
                      {ago(conversation.lastMessageAt ?? conversation.createdAt)}
                    </span>
                    {/*
                      The count, where a messaging app puts it: after the time, on
                      the right. "3" is the difference between "something happened"
                      and "three things are waiting for you".
                    */}
                    {newMessages > 0 && (
                      <span
                        className="unread-count"
                        aria-label={`${String(newMessages)} new messages`}
                      >
                        {Math.min(newMessages, 99)}
                      </span>
                    )}
                  </span>

                  <span className="queue-tags">
                    <span className={`tag ${status.tone}`}>{status.text}</span>

                    {conversation.tags.map((tag) => (
                      <span key={tag} className="tag">
                        {tag.replaceAll("_", " ")}
                      </span>
                    ))}

                    {/* Shown so an agent can see at a glance that a colleague owns it. */}
                    {conversation.assignedAgentId !== undefined && (
                      <span className="tag">assigned</span>
                    )}

                    {/*
                      Folded onto the tag row rather than given a line of its own.
                      It is context an agent reads once they have already decided
                      to look at this row, and it was costing every row a third of
                      its height to say so.
                    */}
                    <span className="queue-count">
                      {conversation.messageCount} message
                      {conversation.messageCount === 1 ? "" : "s"}
                    </span>
                  </span>
                </span>
              </button>
            </div>
          );
        })}
      </div>
    </nav>
  );
}
